import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Heading,
  Icon,
  Input,
  Select,
  SimpleGrid,
  Skeleton,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Tag,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { useAuth0 } from "@auth0/auth0-react";
import { useKnoxMetrics } from "../../../lib/hooks";
import { SidebarContext } from "../../../contexts/SidebarContext";
import Card from "../../../components/card/Card";
import { MdLock, MdTrendingUp, MdPhone, MdPeople, MdDateRange } from "react-icons/md";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import moment from "moment";

function KnoxMetrics() {
  const { user, isLoading: userLoading } = useAuth0();
  const { sidebarCollapsed } = useContext(SidebarContext);
  const [mounted, setMounted] = useState(false);
  const [rangeFilter, setRangeFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  useEffect(() => {
    const token = localStorage.getItem("auth0_token");
    if (user && !userLoading && token) setMounted(true);
  }, [user, userLoading]);

  const { knoxMetrics, isKnoxMetricsLoading } = useKnoxMetrics(
    mounted,
    rangeFilter,
    fromDate,
    toDate
  );

  const metricsData = knoxMetrics?.body;
  const bgColor = useColorModeValue("white", "#1B254B");
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");

  const COLORS = ["#4318FF", "#6AD2FF", "#E9A0FF", "#FFB547", "#01B574"];

  const handleRangeChange = (value) => {
    setRangeFilter(value);
    if (value !== "custom") {
      setFromDate("");
      setToDate("");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-AE", {
      style: "currency",
      currency: "AED",
      minimumFractionDigits: 2,
    }).format(value || 0);
  };

  const cleanPhoneName = (name) => {
    if (!name) return name;
    return name.replace(/samsung galaxy /i, "").replace(/samsung /i, "");
  };

  return (
    <Box
      position="fixed"
      top="80px"
      left={{ base: 0, xl: sidebarCollapsed ? "80px" : "290px" }}
      right="0"
      bottom="0"
      display="flex"
      flexDirection="column"
      p={{ base: "20px", md: "30px" }}
      pe="20px"
      overflow="auto"
    >
      {/* Header and Filters */}
      <Flex mb={6} direction={{ base: "column", md: "row" }} gap={4} alignItems={{ base: "stretch", md: "center" }}>
        <Heading size="lg" flex="1">
          Knox Metrics Dashboard
        </Heading>
        <Flex gap={3} direction={{ base: "column", sm: "row" }} alignItems="stretch">
          <Select
            value={rangeFilter}
            onChange={(e) => handleRangeChange(e.target.value)}
            w={{ base: "full", sm: "200px" }}
            bg={bgColor}
          >
            <option value="all">All Time</option>
            <option value="last30">Last 30 Days</option>
            <option value="previous-month">Previous Month</option>
            <option value="custom">Custom Range</option>
          </Select>
          {rangeFilter === "custom" && (
            <>
              <Input
                type="date"
                placeholder="From"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                w={{ base: "full", sm: "150px" }}
                bg={bgColor}
              />
              <Input
                type="date"
                placeholder="To"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                w={{ base: "full", sm: "150px" }}
                bg={bgColor}
              />
            </>
          )}
        </Flex>
      </Flex>

      {/* Range Display */}
      {metricsData?.range && (
        <Flex mb={4} alignItems="center" gap={2}>
          <Icon as={MdDateRange} color="gray.500" />
          <Text fontSize="sm" color="gray.600">
            {metricsData.range}
          </Text>
        </Flex>
      )}

      {/* Summary Cards */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={5} mb={6}>
        <Skeleton isLoaded={!isKnoxMetricsLoading} borderRadius="20px">
          <Card bg={bgColor} p={6} borderRadius="20px" boxShadow="md">
            <Stat>
              <Flex alignItems="center" mb={2}>
                <Icon as={MdLock} w={6} h={6} color="red.500" mr={2} />
                <StatLabel fontSize="sm" color="gray.500">
                  Total Locked Phones
                </StatLabel>
              </Flex>
              <StatNumber fontSize="3xl" fontWeight="bold" color={textColor}>
                {metricsData?.summary?.totalLockedPhones || 0}
              </StatNumber>
            </Stat>
          </Card>
        </Skeleton>

        <Skeleton isLoaded={!isKnoxMetricsLoading} borderRadius="20px">
          <Card bg={bgColor} p={6} borderRadius="20px" boxShadow="md">
            <Stat>
              <Flex alignItems="center" mb={2}>
                <Icon as={MdTrendingUp} w={6} h={6} color="orange.500" mr={2} />
                <StatLabel fontSize="sm" color="gray.500">
                  Total Unpaid on Locked
                </StatLabel>
              </Flex>
              <StatNumber fontSize="2xl" fontWeight="bold" color={textColor}>
                {formatCurrency(metricsData?.summary?.totalUnpaidOnLocked)}
              </StatNumber>
            </Stat>
          </Card>
        </Skeleton>

        <Skeleton isLoaded={!isKnoxMetricsLoading} borderRadius="20px">
          <Card bg={bgColor} p={6} borderRadius="20px" boxShadow="md">
            <Stat>
              <Flex alignItems="center" mb={2}>
                <Icon as={MdTrendingUp} w={6} h={6} color="blue.500" mr={2} />
                <StatLabel fontSize="sm" color="gray.500">
                  Upcoming Due Amount
                </StatLabel>
              </Flex>
              <StatNumber fontSize="2xl" fontWeight="bold" color={textColor}>
                {formatCurrency(metricsData?.upcomingDue)}
              </StatNumber>
            </Stat>
          </Card>
        </Skeleton>

        <Skeleton isLoaded={!isKnoxMetricsLoading} borderRadius="20px">
          <Card bg={bgColor} p={6} borderRadius="20px" boxShadow="md">
            <Stat>
              <Flex alignItems="center" mb={2}>
                <Icon as={MdTrendingUp} w={6} h={6} color="green.500" mr={2} />
                <StatLabel fontSize="sm" color="gray.500">
                  Due Today
                </StatLabel>
              </Flex>
              <StatNumber fontSize="2xl" fontWeight="bold" color={textColor}>
                {formatCurrency(metricsData?.dueToday)}
              </StatNumber>
            </Stat>
          </Card>
        </Skeleton>
      </SimpleGrid>

      {/* Overdue Buckets */}
      <Skeleton isLoaded={!isKnoxMetricsLoading} borderRadius="20px" mb={6}>
        <Card bg={bgColor} p={6} borderRadius="20px" boxShadow="md">
          <Heading size="md" mb={4} color={textColor}>
            Overdue Days Summary
          </Heading>
          <Flex gap={3} flexWrap="wrap">
            {metricsData?.overdueBuckets?.map((bucket, index) => (
              <Tag
                key={index}
                size="lg"
                colorScheme={
                  bucket.label === "1-7"
                    ? "yellow"
                    : bucket.label === "8-15"
                    ? "orange"
                    : bucket.label === "16-30"
                    ? "red"
                    : "purple"
                }
                borderRadius="full"
                px={4}
                py={2}
              >
                <Text fontWeight="bold" mr={2}>
                  {bucket.label} days:
                </Text>
                <Text>{bucket.count}</Text>
              </Tag>
            ))}
          </Flex>
        </Card>
      </Skeleton>

      {/* Charts Grid */}
      <Grid
        templateColumns={{ base: "1fr", lg: "repeat(2, 1fr)" }}
        gap={6}
        mb={6}
      >
        {/* Locked Phones per Phone Type */}
        <Skeleton isLoaded={!isKnoxMetricsLoading} borderRadius="20px">
          <Card bg={bgColor} p={6} borderRadius="20px" boxShadow="md" height="400px">
            <Heading size="md" mb={4} color={textColor}>
              Locked Phones per Phone Type
            </Heading>
            {metricsData?.lockedByItem?.length > 0 ? (
              <ResponsiveContainer width="100%" height="85%">
                <BarChart
                  data={metricsData.lockedByItem.map((item) => ({
                    ...item,
                    name: cleanPhoneName(item.name),
                  }))}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="name"
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    interval={0}
                    tick={(props) => {
                      const { x, y, payload } = props;
                      const name = payload.value;
                      const truncated = name.length > 10 ? name.substring(0, 10) + '...' : name;
                      return (
                        <g transform={`translate(${x},${y})`}>
                          <title>{name}</title>
                          <text
                            x={0}
                            y={0}
                            dy={16}
                            textAnchor="end"
                            fill="#666"
                            transform="rotate(-45)"
                            fontSize="12"
                          >
                            {truncated}
                          </text>
                        </g>
                      );
                    }}
                  />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="count" fill="#FF6B6B" name="Locked Phones" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Flex justify="center" align="center" height="85%">
                <Text color="gray.500">No data available</Text>
              </Flex>
            )}
          </Card>
        </Skeleton>

        {/* Phones Sold per EMI Tenure */}
        <Skeleton isLoaded={!isKnoxMetricsLoading} borderRadius="20px">
          <Card bg={bgColor} p={6} borderRadius="20px" boxShadow="md" height="400px">
            <Heading size="md" mb={4} color={textColor}>
              Phones Sold per EMI Tenure
            </Heading>
            {metricsData?.salesByTenure?.length > 0 ? (
              <ResponsiveContainer width="100%" height="85%">
                <BarChart data={metricsData.salesByTenure}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="count" fill="#4318FF" name="Count" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Flex justify="center" align="center" height="85%">
                <Text color="gray.500">No data available</Text>
              </Flex>
            )}
          </Card>
        </Skeleton>

        {/* Sales per Camp */}
        <Skeleton isLoaded={!isKnoxMetricsLoading} borderRadius="20px">
          <Card bg={bgColor} p={6} borderRadius="20px" boxShadow="md" height="400px">
            <Heading size="md" mb={4} color={textColor}>
              Sales per Camp
            </Heading>
            {metricsData?.salesByCamp?.length > 0 ? (
              <ResponsiveContainer width="100%" height="85%">
                <BarChart data={metricsData.salesByCamp} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={100} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="count" fill="#6AD2FF" name="Count" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Flex justify="center" align="center" height="85%">
                <Text color="gray.500">No data available</Text>
              </Flex>
            )}
          </Card>
        </Skeleton>

        {/* Sales per Agent */}
        <Skeleton isLoaded={!isKnoxMetricsLoading} borderRadius="20px">
          <Card bg={bgColor} p={6} borderRadius="20px" boxShadow="md" height="400px">
            <Heading size="md" mb={4} color={textColor}>
              Sales per Agent
            </Heading>
            {metricsData?.salesByAgent?.length > 0 ? (
              <ResponsiveContainer width="100%" height="85%">
                <BarChart data={metricsData.salesByAgent}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="count" fill="#E9A0FF" name="Count" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Flex justify="center" align="center" height="85%">
                <Text color="gray.500">No data available</Text>
              </Flex>
            )}
          </Card>
        </Skeleton>
      </Grid>

      {/* Sales Trend - Full Width */}
      <Skeleton isLoaded={!isKnoxMetricsLoading} borderRadius="20px" mb={6}>
        <Card bg={bgColor} p={6} borderRadius="20px" boxShadow="md" height="400px">
          <Heading size="md" mb={4} color={textColor}>
            Sales Trend Past 7 Days
            {metricsData?.salesTrend?.length > 0 && (
              <Text as="span" fontSize="md" color="gray.500" ml={2}>
                ({metricsData.salesTrend.reduce((sum, item) => sum + (item.count || 0), 0)})
              </Text>
            )}
          </Heading>
          {metricsData?.salesTrend?.length > 0 ? (
            <ResponsiveContainer width="100%" height="85%">
              <LineChart data={metricsData.salesTrend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(date) => moment(date).format("MMM DD")}
                />
                <YAxis />
                <Tooltip
                  labelFormatter={(date) => moment(date).format("MMMM DD, YYYY")}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="#01B574"
                  strokeWidth={2}
                  name="Sales Count"
                  dot={{ fill: "#01B574" }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <Flex justify="center" align="center" height="85%">
              <Text color="gray.500">No data available</Text>
            </Flex>
          )}
        </Card>
      </Skeleton>
    </Box>
  );
}

export default KnoxMetrics;
